import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, timeout: 20000 });

// ---------------------------------------------------------------------------
// Admin token helpers (single shared admin password -> bearer token)
// ---------------------------------------------------------------------------
const ADMIN_TOKEN_KEY = "ibp_admin_token";

export const getAdminToken = () =>
  (typeof localStorage !== "undefined" && localStorage.getItem(ADMIN_TOKEN_KEY)) || "";

export const setAdminToken = (token) => {
  if (typeof localStorage === "undefined") return;
  if (token) localStorage.setItem(ADMIN_TOKEN_KEY, token);
  else localStorage.removeItem(ADMIN_TOKEN_KEY);
};

const authHeader = () => {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getConfig = async () => {
  const { data } = await client.get("/config");
  return data;
};

export const updateConfig = async (payload) => {
  const { data } = await client.put("/config", payload, { headers: authHeader() });
  return data;
};

export const sendContact = async (payload) => {
  const { data } = await client.post("/contact", payload);
  return data;
};

export const submitLiveReviewInterest = async (payload) => {
  const { data } = await client.post("/live-review-interest", payload);
  return data;
};

// --- Admin auth ---
export const adminLogin = async (password) => {
  const { data } = await client.post("/admin/login", { password });
  return data;
};

export const adminVerify = async () => {
  const { data } = await client.get("/admin/verify", { headers: authHeader() });
  return data;
};

// --- Admin contact inbox ---
export const getContacts = async () => {
  const { data } = await client.get("/contact", { headers: authHeader() });
  return data;
};

export const deleteContact = async (id) => {
  const { data } = await client.delete(`/contact/${id}`, { headers: authHeader() });
  return data;
};

export const getLiveReviewInterest = async () => {
  const { data } = await client.get("/admin/live-review-interest", { headers: authHeader() });
  return data;
};

export const updateLiveReviewInterestStatus = async (id, status) => {
  const { data } = await client.patch(
    `/admin/live-review-interest/${id}`,
    { status },
    { headers: authHeader() }
  );
  return data;
};

export const deleteLiveReviewInterest = async (id) => {
  const { data } = await client.delete(`/admin/live-review-interest/${id}`, {
    headers: authHeader(),
  });
  return data;
};

// ---------------------------------------------------------------------------
// Analytics events + Diagnostic
// ---------------------------------------------------------------------------
const SESSION_KEY = "ibp_session_id";

export const getSessionId = () => {
  if (typeof sessionStorage === "undefined") return "";
  let s = sessionStorage.getItem(SESSION_KEY);
  if (!s) {
    s =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `s_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(SESSION_KEY, s);
  }
  return s;
};

export const trackEvent = async (event, props = {}) => {
  try {
    await client.post("/events", { event, session_id: getSessionId(), props });
  } catch (e) {
    // analytics must never break UX; surface only in development for debugging
    if (process.env.NODE_ENV !== "production") {
      console.debug("trackEvent failed:", event, e && e.message);
    }
  }
};

export const getDiagnosticQuestions = async () => {
  const { data } = await client.get("/diagnostic/questions");
  return data.questions;
};

export const submitDiagnostic = async (answers) => {
  const { data } = await client.post("/diagnostic/submit", { answers });
  return data;
};

export const submitDiagnosticLead = async (email, attemptId) => {
  const { data } = await client.post("/diagnostic/lead", {
    email,
    attempt_id: attemptId,
  });
  return data;
};

export const getDiagnosticPlanPreview = async (attemptId) => {
  const { data } = await client.get(`/diagnostic/plan-preview/${attemptId}`);
  return data;
};

// --- Admin: diagnostic leads + email logs ---
export const getLeads = async () => {
  const { data } = await client.get("/admin/leads", { headers: authHeader() });
  return data;
};

export const getEmailLogs = async () => {
  const { data } = await client.get("/admin/email-logs", { headers: authHeader() });
  return data;
};

// ---------------------------------------------------------------------------
// Study accounts, legacy access-code migration, and admin question bank
// ---------------------------------------------------------------------------
const STUDY_TOKEN_KEY = "ibp_study_token";
const STUDY_EMAIL_KEY = "ibp_study_email";
const STUDY_LICENSE_KEY = "ibp_study_license";

export const getStudyToken = () =>
  (typeof localStorage !== "undefined" && localStorage.getItem(STUDY_TOKEN_KEY)) || "";
export const getStudyEmail = () =>
  (typeof localStorage !== "undefined" && localStorage.getItem(STUDY_EMAIL_KEY)) || "";
export const getStudyLicense = () => {
  if (typeof localStorage === "undefined") return null;
  try {
    return JSON.parse(localStorage.getItem(STUDY_LICENSE_KEY) || "null");
  } catch (e) {
    return null;
  }
};
export const setStudyLicense = (lic) => {
  if (typeof localStorage === "undefined") return;
  if (lic) localStorage.setItem(STUDY_LICENSE_KEY, JSON.stringify(lic));
  else localStorage.removeItem(STUDY_LICENSE_KEY);
};
export const setStudyAuth = (token, email) => {
  if (typeof localStorage === "undefined") return;
  if (token) {
    localStorage.setItem(STUDY_TOKEN_KEY, token);
    localStorage.setItem(STUDY_EMAIL_KEY, email || "");
  } else {
    localStorage.removeItem(STUDY_TOKEN_KEY);
    localStorage.removeItem(STUDY_EMAIL_KEY);
    localStorage.removeItem(STUDY_LICENSE_KEY);
  }
};
const studyHeader = () => {
  const t = getStudyToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const studyLogin = async (email, accessCode) => {
  const { data } = await client.post("/study/login", { email, access_code: accessCode });
  return data;
};
export const requestStudentSignInCode = async (email, firstName = "") => {
  const { data } = await client.post("/student/auth/request-code", {
    email,
    first_name: firstName,
  });
  return data;
};
export const verifyStudentSignInCode = async (email, code) => {
  const { data } = await client.post("/student/auth/verify-code", { email, code });
  return data;
};
export const resendAccessCode = async (email) => {
  const { data } = await client.post("/access/resend", { email });
  return data;
};
export const getCoverage = async () => {
  const { data } = await client.get("/coverage");
  return data;
};
export const getTestimonials = async () => {
  const { data } = await client.get("/testimonials");
  return data;
};
export const getStudyMe = async () => {
  const { data } = await client.get("/study/me", { headers: studyHeader() });
  return data;
};
export const getLearningProgress = async () => {
  const { data } = await client.get("/study/learning/progress", { headers: studyHeader() });
  return data;
};
export const updateLearningProgress = async (lessonId, completed) => {
  const { data } = await client.post(
    "/study/learning/progress",
    { lesson_id: lessonId, completed },
    { headers: studyHeader() }
  );
  return data;
};
export const getStudyQuestions = async (params = {}) => {
  const { data } = await client.get("/study/questions", { params, headers: studyHeader() });
  return data.questions;
};
export const submitAnswer = async (questionId, selected, mode) => {
  const { data } = await client.post(
    "/study/answer",
    { question_id: questionId, selected, mode },
    { headers: studyHeader() }
  );
  return data;
};
export const toggleBookmark = async (questionId, bookmarked) => {
  const { data } = await client.post(
    "/study/bookmark",
    { question_id: questionId, bookmarked },
    { headers: studyHeader() }
  );
  return data;
};
export const reportQuestion = async (questionId, reason, details) => {
  const { data } = await client.post(
    "/study/question-reports",
    { question_id: questionId, reason, details },
    { headers: studyHeader() }
  );
  return data;
};
export const getStudyDashboard = async () => {
  const { data } = await client.get("/study/dashboard", { headers: studyHeader() });
  return data;
};
export const getStudentFeedback = async () => {
  const { data } = await client.get("/study/feedback", { headers: studyHeader() });
  return data;
};
export const submitStudentFeedback = async (payload) => {
  const { data } = await client.post("/study/feedback", payload, { headers: studyHeader() });
  return data;
};

// --- Question Coach (grounded, per-student capped) ---
export const askTutor = async (questionId, message) => {
  const { data } = await client.post(
    "/study/tutor",
    { question_id: questionId, message },
    { headers: studyHeader() }
  );
  return data;
};

// --- Flashcards / vocabulary mode (AI-generated study aid) ---
export const getFlashcards = async () => {
  const { data } = await client.get("/study/flashcards", { headers: studyHeader() });
  return data;
};
export const generateFlashcards = async () => {
  const { data } = await client.post(
    "/study/flashcards/generate",
    {},
    { headers: studyHeader() }
  );
  return data;
};
export const setFlashcardProgress = async (cardId, status) => {
  const { data } = await client.post(
    "/study/flashcards/progress",
    { card_id: cardId, status },
    { headers: studyHeader() }
  );
  return data;
};
// Admin flashcard management
export const getAdminFlashcards = async () => {
  const { data } = await client.get("/admin/flashcards", { headers: authHeader() });
  return data;
};
export const editFlashcard = async (cardId, fields) => {
  const { data } = await client.patch(`/admin/flashcards/${cardId}`, fields, { headers: authHeader() });
  return data;
};
export const deleteFlashcard = async (cardId) => {
  const { data } = await client.delete(`/admin/flashcards/${cardId}`, { headers: authHeader() });
  return data;
};

// --- Structured study plans ---
export const createStudyPlan = async (planType, examDate) => {
  const { data } = await client.post(
    "/study/plan",
    { plan_type: planType, exam_date: examDate || "" },
    { headers: studyHeader() }
  );
  return data.plan;
};
export const getStudyPlan = async () => {
  const { data } = await client.get("/study/plan", { headers: studyHeader() });
  return data.plan;
};
export const togglePlanDay = async (day, done) => {
  const { data } = await client.post(
    "/study/plan/toggle-day",
    { day, done },
    { headers: studyHeader() }
  );
  return data.plan;
};
export const deleteStudyPlan = async () => {
  const { data } = await client.delete("/study/plan", { headers: studyHeader() });
  return data;
};

// --- Timed Review Sprints ---
export const getSprintOverview = async () => {
  const { data } = await client.get("/study/sprints/overview", { headers: studyHeader() });
  return data;
};
export const startSprint = async (sprintType) => {
  const { data } = await client.post(
    "/study/sprints/start",
    { sprint_type: sprintType },
    { headers: studyHeader() }
  );
  return data;
};
export const getActiveSprint = async (attemptId) => {
  const { data } = await client.get(`/study/sprints/active/${attemptId}`, {
    headers: studyHeader(),
  });
  return data;
};
export const saveSprintAnswer = async (attemptId, questionId, selected) => {
  const { data } = await client.post(
    "/study/sprints/answer",
    { attempt_id: attemptId, question_id: questionId, selected },
    { headers: studyHeader() }
  );
  return data;
};
export const submitSprint = async (attemptId, answers, autoSubmitted = false) => {
  const { data } = await client.post(
    "/study/sprints/submit",
    { attempt_id: attemptId, answers, auto_submitted: autoSubmitted },
    { headers: studyHeader() }
  );
  return data;
};
export const getSprintResults = async (attemptId) => {
  const { data } = await client.get(`/study/sprints/results/${attemptId}`, {
    headers: studyHeader(),
  });
  return data;
};

// Admin question bank + access codes
export const getStudentAccounts = async () => {
  const { data } = await client.get("/admin/student-accounts", { headers: authHeader() });
  return data;
};
export const getAdminQuestions = async (params = {}) => {
  const { data } = await client.get("/admin/questions", { params, headers: authHeader() });
  return data;
};
export const importQuestions = async (questions) => {
  const { data } = await client.post(
    "/admin/questions/import",
    { questions },
    { headers: authHeader() }
  );
  return data;
};
export const patchQuestion = async (qid, fields) => {
  const { data } = await client.patch(`/admin/questions/${qid}`, fields, { headers: authHeader() });
  return data;
};
export const getAccessCodes = async (params = {}) => {
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v));
  const { data } = await client.get("/admin/access-codes", { params: clean, headers: authHeader() });
  return data;
};
export const createAccessCode = async (payload) => {
  const { data } = await client.post("/admin/access-codes", payload, { headers: authHeader() });
  return data;
};
export const createAccessCodesBatch = async (payload) => {
  const { data } = await client.post("/admin/access-codes/batch", payload, { headers: authHeader() });
  return data;
};
export const updateAccessCode = async (codeId, fields) => {
  const { data } = await client.patch(`/admin/access-codes/${codeId}`, fields, { headers: authHeader() });
  return data;
};
export const deleteAccessCode = async (codeId) => {
  const { data } = await client.delete(`/admin/access-codes/${codeId}`, { headers: authHeader() });
  return data;
};
export const getAccessCodeUsage = async (codeId) => {
  const { data } = await client.get(`/admin/access-codes/${codeId}/usage`, { headers: authHeader() });
  return data;
};

// --- Admin: Shopify webhook deliveries ---
export const getWebhookDeliveries = async () => {
  const { data } = await client.get("/admin/webhook-deliveries", { headers: authHeader() });
  return data;
};
export const getRevenueSummary = async () => {
  const { data } = await client.get("/admin/revenue-summary", { headers: authHeader() });
  return data;
};
export const getAnalytics = async () => {
  const { data } = await client.get("/admin/analytics", { headers: authHeader() });
  return data;
};
export const resendWebhookDelivery = async (deliveryId) => {
  const { data } = await client.post(
    `/admin/webhook-deliveries/${deliveryId}/resend`,
    {},
    { headers: authHeader() }
  );
  return data;
};

// --- Admin: AI tutor usage ---
export const getTutorUsage = async () => {
  const { data } = await client.get("/admin/tutor-usage", { headers: authHeader() });
  return data;
};

// --- Admin: quality inbox ---
export const getQuestionReports = async () => {
  const { data } = await client.get("/admin/quality/question-reports", { headers: authHeader() });
  return data;
};
export const updateQuestionReport = async (reportId, status) => {
  const { data } = await client.patch(
    `/admin/quality/question-reports/${reportId}`,
    { status },
    { headers: authHeader() }
  );
  return data;
};
export const getStudentFeedbackAdmin = async () => {
  const { data } = await client.get("/admin/quality/feedback", { headers: authHeader() });
  return data;
};
export const updateStudentFeedbackAdmin = async (feedbackId, status) => {
  const { data } = await client.patch(
    `/admin/quality/feedback/${feedbackId}`,
    { status },
    { headers: authHeader() }
  );
  return data;
};

export const downloadCsv = async (path, filename) => {
  const res = await client.get(path, { headers: authHeader(), responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export default client;
